import React, {useCallback, useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';

import {Button, Checkbox, CheckboxProps, Modal, TextBox} from '@veneer/core';

import {PartnerGroup} from '@br/idm-api';
import {DetailValue, useCustomerApi, useFormatDetails, useTenantContext} from '@br/idm-components';
import {A, StepActions} from '@br/mg-components';

import {assertIsNotNullish} from '../../util';

const AddPartner = ({onAddPartner}: {onAddPartner: (partnerGroup: PartnerGroup) => void}): JSX.Element => {
    const {t} = useTranslation(['settings']);
    const {partnerApi} = useCustomerApi();
    const {tenant} = useTenantContext();
    const formatDetails = useFormatDetails();

    // Base state
    const [showModal, setShowModal] = useState(false);
    const handleShowModal = useCallback(() => setShowModal(true), []);

    const onClose = useCallback(() => {
        // Base state
        setShowModal(false);
        // Step 1
        setPartnerCode('');
        // Step 2
        setIsLoading(false);
        setError('');
        // Step 3
        setPartner(null);
        setConfirmation(false);
    }, []);

    // Step 1 - Inform customer, allow them to enter partner code
    const [partnerCode, setPartnerCode] = useState('');

    // Step 2 - Submit partner code, show loading spinner
    const [loading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const getPartner = useCallback(() => {
        setIsLoading(true);
        setError('');
        partnerApi.partnerLookupRetrieve({lookup: partnerCode}).then(
            partnerGroup => {
                setPartner(partnerGroup);
                setIsLoading(false);
            },
            () => {
                setError(t('settings:couldNotLocatePartner', 'Could not locate partner'));
                setIsLoading(false);
            }
        );
    }, [partnerApi, partnerCode, t]);

    // Step 3 - Show partner details, allow them to confirm
    const [partner, setPartner] = useState<PartnerGroup | null>(null);
    const confirmDetails: DetailValue[] = [
        {label: t('settings:tenantName', 'Tenant name'), value: tenant.name},
        {label: t('settings:tenantId', 'Tenant ID'), value: tenant.shortId},
        {label: t('settings:partnerName', 'Partner name'), value: partner?.name},
        {label: t('settings:partnerCode', 'Partner code'), value: partner?.shortId},
    ];
    const [confirmation, setConfirmation] = useState<boolean>(false);
    const handleConfirmCheckbox = useCallback<Required<CheckboxProps>['onChange']>(
        e => setConfirmation(e.target.checked),
        []
    );

    const onBack = useCallback(() => {
        setPartner(null);
        setConfirmation(false);
    }, []);

    const handleAddPartner = useCallback(() => {
        assertIsNotNullish(partner);
        onAddPartner(partner);
        onClose();
    }, [onAddPartner, onClose, partner]);

    return (
        <>
            <Button
                style={{height: '48px'}}
                appearance="secondary"
                className="ms-5"
                onClick={handleShowModal}
            >
                {t('settings:addPartner', 'Add partner')}
            </Button>
            <Modal
                id="add-partner-modal"
                onClose={onClose}
                show={showModal}
                title={t('settings:addPartner', 'Add partner')}
                footer={
                    partner === null ? (
                        <StepActions
                            loading={loading}
                            cancel={onClose}
                            next={getPartner}
                            nextDisabled={partnerCode.length !== 8}
                        />
                    ) : (
                        <StepActions
                            loading={loading}
                            back={onBack}
                            cancel={onClose}
                            next={handleAddPartner}
                            nextText={t('settings:confirm', 'Confirm')}
                            nextDisabled={!confirmation}
                            finalStep
                        />
                    )
                }
            >
                {partner === null ? (
                    <>
                        <Trans
                            i18nKey="settings:partnerDescription"
                            shouldUnescape
                        >
                            <p className="pb-2">
                                Adding a partner to the tenant &quot;{{name: tenant.name}}&quot; gives them permission
                                to view the tenant data as outlined{' '}
                                <A
                                    href="https://kb.bromium.com/article/What-Tenant-information-does-a-Partner-have-access-to/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    on our information page
                                </A>
                                .
                            </p>
                            <p className="pb-2">
                                If you are not comfortable giving a partner this level of access, you should not submit
                                this form.
                            </p>
                            <p className="pb-2">
                                You can revoke access at any time by returning to the tenant settings page.
                            </p>
                        </Trans>

                        <TextBox
                            value={partnerCode}
                            id="partner-code"
                            label={t('settings:partnerCode', 'Partner code')}
                            maxLength={8}
                            onChange={setPartnerCode}
                            helperText={error}
                            error={!!error}
                        />
                    </>
                ) : (
                    <>
                        <div className="mx-n2 d-flex flex-wrap">{confirmDetails.map(formatDetails)}</div>
                        <hr className="mt-2 mb-4" />
                        <Trans i18nKey="settings:partnerConfirmation">
                            <p className="pb-2">
                                By submitting this form, you are giving this partner permission to view the information
                                outlined{' '}
                                <A
                                    href="https://kb.bromium.com/article/What-Tenant-information-does-a-Partner-have-access-to/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    on our information page
                                </A>
                                .
                            </p>
                            <p>
                                If you are not sure, please{' '}
                                <A
                                    href="https://support.hpwolf.com/s/contact"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    contact us
                                </A>{' '}
                                for more information and do not proceed.
                            </p>
                        </Trans>
                        <Checkbox
                            className="mt-4"
                            label={t(
                                'settings:partnerConfirmationLabel',
                                'Confirm that you understand and agree to the above statement'
                            )}
                            checked={confirmation}
                            onChange={handleConfirmCheckbox}
                        />
                    </>
                )}
            </Modal>
        </>
    );
};

export default AddPartner;
